import type { RouteLocationRaw } from 'vue-router';

import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import { useFlagStore } from '@/stores/flag';

const NOTIFICATION_DELAY = 5000;

export type ToastVariant = 'primary' | 'success' | 'warning' | 'error';

/**
 * A toast's `actionText` link target: a URL, a route to navigate to, or a
 * click handler.
 */
export type ToastActionTo = string | RouteLocationRaw | (() => void);

export interface SetToastPayload {
  htmlMessage?: string | null;
  message?: string;
  subtext?: string;
  type?: ToastVariant;
  delay?: number;
  actionText?: string;
  actionTo?: ToastActionTo;
  sameLine?: boolean;
  buttonText?: string;
  buttonAction?: () => void;
  onDismiss?: () => void;
  autoClear?: boolean;
}

export interface Toast {
  id: number;
  htmlMessage?: string | null;
  message?: string;
  subtext?: string;
  type?: ToastVariant;
  actionText?: string;
  actionTo?: ToastActionTo;
  sameLine?: boolean;
  buttonText?: string;
  buttonAction?: () => void;
  onDismiss?: () => void;
}

export interface MessagePopupProps {
  showMessagePopup?: boolean;
  title?: string;
  message?: string;
  buttonText?: string;
  mediaPostUrl?: string;
  clickHandle?: (() => void) | null;
}

export interface ConfirmOptions {
  confirmAlias?: string;
  confirmType?: string;
  onConfirm?: () => unknown;
  onCancel?: () => unknown;
  cancelAlias?: string;
  uncancellable?: boolean;
  largerText?: boolean;
  platform?: string;
}

export interface ConfirmMessageState {
  show: boolean;
  title?: string | null;
  message?: string | null;
  confirmAlias?: string;
  confirmType?: string;
  confirmCallback?: () => unknown;
  cancelAlias?: string;
  cancelCallback?: () => unknown;
  uncancellable?: boolean;
  largerText?: boolean;
  platform?: string;
  resolve?: (value: boolean) => void;
  reject?: (reason?: unknown) => void;
}

export const useNotificationStore = defineStore('notification', () => {
  const flagStore = useFlagStore();

  let maxToastId = 0;
  const toasts = ref<Toast[]>([]);
  const platformIsDown = ref<boolean | undefined | null>(null);
  const messagePopupProps = ref<MessagePopupProps>({});
  const confirmMessage = ref<ConfirmMessageState>({
    show: false,
  });

  function clearToasts({ id }: { id?: number } = {}) {
    toasts.value = toasts.value.filter((n) => n.id !== id);
  }

  function setToast(payload?: SetToastPayload | null) {
    const {
      htmlMessage,
      message,
      subtext,
      type,
      delay = NOTIFICATION_DELAY,
      actionText,
      actionTo,
      sameLine,
      buttonText,
      buttonAction,
      onDismiss,
      autoClear = true,
    } = payload ?? {};

    maxToastId += 1;
    const id = maxToastId;

    toasts.value.push({
      id,
      htmlMessage,
      message,
      subtext,
      type,
      actionText,
      actionTo,
      sameLine,
      buttonText,
      buttonAction,
      onDismiss,
    });

    if (autoClear) {
      setTimeout(() => {
        clearToasts({ id });
      }, delay);
    }
    return id;
  }

  function setMessagePopup({
    showMessagePopup,
    title = '',
    message = '',
    buttonText = '',
    mediaPostUrl = '',
    clickHandle = null,
  }: MessagePopupProps) {
    messagePopupProps.value = {
      showMessagePopup,
      title,
      message,
      buttonText,
      mediaPostUrl,
      clickHandle,
    };
  }

  function confirm(title?: string | null, message?: string | null, options?: ConfirmOptions) {
    let confirmMessageResolve!: (value: boolean) => void;
    let confirmMessageReject!: (reason?: unknown) => void;

    const promise = new Promise<boolean>((resolve, reject) => {
      confirmMessageResolve = resolve;
      confirmMessageReject = reject;
    });

    confirmMessage.value = {
      show: true,
      title,
      message,
      confirmAlias: options?.confirmAlias,
      confirmType: options?.confirmType,
      confirmCallback: options?.onConfirm || (() => {}),
      cancelAlias: options?.cancelAlias,
      cancelCallback: options?.onCancel || (() => {}),
      uncancellable: !!options?.uncancellable,
      largerText: options?.largerText,
      platform: options?.platform,
      resolve: confirmMessageResolve,
      reject: confirmMessageReject,
    };

    return promise;
  }

  watch(
    () => flagStore.flags?.platformOutage,
    (status) => {
      platformIsDown.value = status;
    },
  );

  return {
    toasts,
    platformIsDown,
    setToast,
    clearToasts,
    messagePopupProps,
    setMessagePopup,
    confirm,
    confirmMessage,
  };
});
